import { Component, EventEmitter, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DateTimeUtils } from '@blcloud/bl-common';
import { IDmContentInfo, IDmIssueSetting, IImageUploadInfo, ITicketSetting } from '@blcloud/bl-datamodel';
import { ContractConst } from '@blcloud/bl-datamodel/const/contract';
import { DmExtractMenuDiv } from '@blcloud/bl-datamodel/enum/approach/dm-extract-menu-div';
import { DmInvitationMediaDiv } from '@blcloud/bl-datamodel/enum/approach/dm-invitation-media-div';
import { DmSnsMsgBodyTypeDiv } from '@blcloud/bl-datamodel/enum/approach/dm-sns-msg-body-type-div';
import { LabelUseCategory } from '@blcloud/bl-datamodel/enum/common/label-use-category';
import { LineMsgTemplateType } from '@blcloud/bl-datamodel/enum/sns/line-msg-template-type';
import { SnsDiv } from '@blcloud/bl-datamodel/enum/sns/sns-div';
import { ApproachAnnounceDetailComponent, DmExtractMenuCode, EditMode, TicketIssueGuideComponent } from '@blcloud/bl-ng-share-module';
import {
  AbstractBlModal,
  BlDialogService,
  BlFormGroup,
  BlModalService,
  BlSelectComponent,
  BlValidatorService,
  FormGroupOption,
  ModalButtonColor,
  ModalButtonSetting,
  ModalButtonType,
  ModalReason,
  ModalSize,
  Rules
} from '@blcloud/bl-ng-ui-component';
import {
  cloneDeep as _cloneDeep,
  delay as _delay,
  filter as _filter,
  find as _find,
  forEach as _forEach,
  isEmpty as _isEmpty,
  isNil as _isNil,
  remove as _remove,
  some as _some
} from 'lodash';
import { Observable } from 'rxjs/Observable';
import { forkJoin as ObservableForkJoin } from 'rxjs/observable/forkJoin';
import { of as ObservableOf } from 'rxjs/observable/of';
import { map, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { SnsSendMessageConfirmModalComponent } from '../sns-send-message-confirm-modal/sns-send-message-confirm-modal.component';
import { ISnsExtractCondition, ISnsSendMessageInfo } from '../sns-send-message-confirm-modal/sns-send-message-confirm-modal.interface';
import { SnsSendMessageContentComponent } from './content/sns-send-message-content.component';
import { DmSnsMsgSettingEx } from './content/sns-send-message-content.interface';
import { SnsSendMessageModalService } from './sns-send-message-modal.service';

/**
 * SNS一斉送信設定モーダル
 * ```
 * content.data: {
 *   // DM案内媒体区分
 *   dmInvitationMediaDiv: string
 * }
 * ```
 */

@Component({
  templateUrl: './sns-send-message-modal.component.html',
  styleUrls: ['./sns-send-message-modal.component.scss'],
  providers: [SnsSendMessageModalService]
})
export class SnsSendMessageModalComponent
  extends AbstractBlModal
  implements OnInit, OnDestroy {

  private readonly onDestroy$ = new EventEmitter();

  /** 案内文の選択（フォーカス制御用） */
  @ViewChild('dmInvitationInitSelect') dmInvitationInitSelect: BlSelectComponent;

  /** SNS一斉送信設定コンテンツコンポーネント */
  @ViewChild('snsSendMessageContent') snsSendMessageContent: SnsSendMessageContentComponent;

  /** DM案内媒体区分 */
  readonly DmInvitationMediaDiv = DmInvitationMediaDiv;

  /** サービスID定義 */
  readonly ContractConst = ContractConst;

  /** ラベルコードカテゴリ */
  labelUseCategory: string[] = [LabelUseCategory.Customer];

  /** SNS送信メッセージ情報 */
  _snsSendMessageInfo = <ISnsSendMessageInfo>{};

  /** SNS抽出条件情報 */
  _snsExtractCondition = <ISnsExtractCondition>{};

  /** LINE公式アカウントリスト */
  lineOfficialAccountList = [];

  /** 案内方法リスト */
  contentInfoListArray = [];

  /** DM案内文情報 */
  dmContentInfo: IDmContentInfo[] = [];

  /** DM発行設定情報 */
  dmIssueSetting = <IDmIssueSetting>{};

  /** チケット情報 */
  ticketSettingList: ITicketSetting[] = [{}];

  /** 案内文の選択非活性フラグ */
  approachSelectDisabledFlg = true;

  /** ボタンタイプ設定 */
  footerButtonType: ModalButtonType = ModalButtonType.OkCancel;

  /** 決定ボタンの設定 */
  okButtonSetting: ModalButtonSetting = { color: ModalButtonColor.Success, label: '送信確認' };

  /** クーポン管理サービスが使用可能か */
  private isAvailableCouponMng = false;

  /** Web予約サービスが有効か */
  isAvailableWebReserveService = false;

  /** ログイン組織が他組織のデータ参照可能か(本社拠点か) */
  isAbleToOtherOrgDataRef = false;

  /** 複数のLINE公式アカウントが選択可能か */
  hasMultipleLineAccount = false;

  /** LINE公式アカウント未選択時時の表示用文字列 */
  private readonly UNSELECTED_LINE_OFFICIAL_ACCOUNT_STR = 'LINE公式アカウント未選択';

  /** フォームグループ */
  blFormGroup = new BlFormGroup();

  /** DM案内文情報ルール */
  dmContentInfoRules: Rules[] = [];

  /** リッチ版LINE案内文を設定するか */
  _isSettingLineRichTemplate = false;

  /** DMSNSメッセージ設定拡張配列 */
  _dmSnsMsgSettingExList: DmSnsMsgSettingEx[] = [];

  /** 入力検証除外対象 (base) */
  private baseIgnoreRules = [
    'DmContentInfo#dmContentNumber',
    'DmContentInfo#dmExtractMenuCode',
    'DmContentInfo#dmInvitationMediaDiv',
    'DmContentInfo#dmContentName',
    'DmSnsMsgSetting#itemNumber',
    'FileManageInfo#fileManageId',
    'FileManageInfo#uploadFileName',
    'FileManageInfo#fileTypeDiv',
    'FileManageInfo#expireDateTime',
    'DmSnsMsgSettingDetail#itemNumber',
  ];

  /** 入力検証除外対象 (LINE リッチテンプレート以外の追加項目) */
  private notLineRithIgnoreRules = [
    'DmSnsMsgSettingDetail#dmSnsMsgTitle',
    'DmSnsMsgSettingDetail#dmSnsMsgBody',
    'DmSnsMsgSetting#lineMsgTemplateType',
  ];

  /** コンストラクタ */
  constructor(
    private service: SnsSendMessageModalService,
    private modalService: BlModalService,
    private dialogService: BlDialogService,
    private validatorService: BlValidatorService,
  ) {
    super();
  }

  /** @override */
  defaultOption() {
    return { size: ModalSize.Large };
  }

  ngOnInit(): void {
    this.initSnsExtractCondition();
  }

  ngOnDestroy() {
    this.hideLoading();
    this.onDestroy$.emit();
  }

  getResults(): any {
    return {};
  }

  /** 初期表示データ取得 */
  onShown() {
    this.data.dmExtractMenuCode = [DmExtractMenuCode.Customize, DmExtractMenuCode.CouponIssue, DmExtractMenuCode.Other];
    this.service.getLoginUserInfo().subscribe(
      (res) => {
        this.data.organizationCode = res.organizationCode;
        // ログイン組織の情報を取得
        this.service.getOrganizationInfo(this.data.organizationCode).subscribe(
          organization => {
            this.isAbleToOtherOrgDataRef = organization.otherOrgDataReference ?
              organization.otherOrgDataReference : false;
            // LINE公式アカウント項目を設定
            this.setLineOfficialAccount();
          });
      }
    );

    // クーポン関連機能が使用可能か判定
    this.service.isAvailableCouponMangeService().subscribe((responses) => {
      this.isAvailableCouponMng = responses;
    });

    // Web予約サービスが使用可能か判定
    this.service.isAvailableWebReserveService().subscribe((responses) => {
      this.isAvailableWebReserveService = responses;
    });

    // DM案内文情報取得
    this.service.dmContentSearch(this.data.dmExtractMenuCode, this.data.dmInvitationMediaDiv)
      .pipe(
        takeUntil(this.onDestroy$)
      )
      .subscribe((res) => {
        if (res.length > 0) {
          // 案内文の選択活性
          this.approachSelectDisabledFlg = false;
          this.dmContentInfo = res;
          this.contentInfoListArray = res.map((extlist) => {
            if (extlist.initConditionDiv) {
              this.updateDmContentInfo(extlist);
            }
            return { code: extlist.dmContentNumber, name: extlist.dmContentName };
          });

          if (!this._snsSendMessageInfo.dmContentInfo) {
            this.updateDmContentInfo(res[0]);
          }
        }
      },
        () => { // エラー時は空のプルダウン表示
        });
    // DM発行設定情報取得
    this.getDmIssueSetting();
    // DM案内文設定のバリデーション取得
    this.service.getDmContentInfoRules().subscribe((res) => {
      this.dmContentInfoRules = res;
      this.setValidate();
    });
  }
  /**
   * 「LINE公式アカウント」項目を設定
   */
  setLineOfficialAccount() {
    // LINE公式アカウント情報取得
    this.service.getSnsShopAccountSetting().subscribe(
      (res) => {
        this.lineOfficialAccountList = res.filter((info) => {
          if (this.data.dmInvitationMediaDiv === DmInvitationMediaDiv.Line && info.snsDiv === SnsDiv.LINE) {
            // ログイン組織に紐づくLINE公式アカウントか
            const isMyOrgSnsShopAccountSetting
              = _some(info.organizationCodeList, orgnizationCode => orgnizationCode === this.data.organizationCode);
            if (isMyOrgSnsShopAccountSetting) {
              // ログイン組織に紐づく公式アカウントを設定
              this._snsSendMessageInfo.snsLinkageId = info.snsLinkageId;
              this._snsSendMessageInfo.snsShopAccountName = info.snsShopAccountName;
            }
            if (this.isAbleToOtherOrgDataRef || isMyOrgSnsShopAccountSetting) {
              // ログイン組織が他組織データ参照可能な場合、
              // もしくはログイン組織に紐づくLINE公式アカウントの場合のみリストに追加
              return true;
            } else {
              return false;
            }
          }
          return false;
        })
          .map((info) => {
            return { code: info.snsLinkageId, name: info.snsShopAccountName };
          });
        if (this.isAbleToOtherOrgDataRef && _isNil(this._snsSendMessageInfo.snsLinkageId) && this.lineOfficialAccountList.length > 0) {
          // ログイン組織が本社拠点かつ自組織に紐づくLINE公式アカウントがない場合はLINE公式アカウント未選択の選択肢を追加する
          const tempLineAccountList = this.lineOfficialAccountList;
          this.lineOfficialAccountList = [];
          this.lineOfficialAccountList.push(
            { code: '', name: this.UNSELECTED_LINE_OFFICIAL_ACCOUNT_STR }, ...tempLineAccountList
          );
          this._snsSendMessageInfo.snsLinkageId = '';
          this._snsSendMessageInfo.snsShopAccountName = this.UNSELECTED_LINE_OFFICIAL_ACCOUNT_STR;
        }
        if (this.lineOfficialAccountList.length > 1) {
          this.hasMultipleLineAccount = true;
        }
      });
  }

  /**
   * LINE公式アカウント選択イベント
   * @param LINE公式アカウントコード
   */
  onChangeLineOfficialAccount(event) {
    const lineOfficialAccount = _find(this.lineOfficialAccountList, data => data.code === event);
    this._snsSendMessageInfo.snsLinkageId = lineOfficialAccount.code;
    this._snsSendMessageInfo.snsShopAccountName = lineOfficialAccount.name;
  }

  /**
   * 案内文選択イベント
   * @param 案内文番号
   */
  onChangeInfoItem(event) {
    // 案内文情報を上書き
    this.updateDmContentInfo(_find(this.dmContentInfo, data => data.dmContentNumber === event));
  }

  /**
   * 案内文の作成イベント
   */
  addApproach(event) {
    // DM案内文設定のバリデーション取得
    const entity: IDmContentInfo = {
      dmExtractMenuCode: this.data.dmExtractMenuCode,
      dmInvitationMediaDiv: this.data.dmInvitationMediaDiv
    };
    const modalData = {
      editMode: EditMode.Add,
      rules: this.dmContentInfoRules,
      entity: entity,
      isGrandsonMode: true
    };

    // DM案内文設定モーダル表示
    this.modalService
      .show(ApproachAnnounceDetailComponent, { data: modalData }).pipe(
        takeUntil(this.onDestroy$)
      )
      .subscribe(modalRef => {
        if (modalRef.reason === ModalReason.Done) {
          _delay(() => {
            // DM案内文情報取得
            this.service.dmContentSearch(this.data.dmExtractMenuCode, this.data.dmInvitationMediaDiv)
              .pipe(
                takeUntil(this.onDestroy$)
              )
              .subscribe((response) => {
                if (response.length > 0) {
                  // 案内文の選択活性
                  this.approachSelectDisabledFlg = false;
                  this.dmContentInfo = response;
                  this.contentInfoListArray = response.map((extlist) => {
                    return { code: extlist.dmContentNumber, name: extlist.dmContentName };
                  });

                  if (!this._snsSendMessageInfo.dmContentInfo) {
                    this.updateDmContentInfo(response[0]);
                  }
                }
              },
                () => { // エラー時は空のプルダウン表示
                });
          }, 1000);
        }
        modalRef.hide(event.target);
      });
  }

  /**
   * DM発行設定情報取得
   */
  private getDmIssueSetting() {
    this.service.dmIssueSearch(this.data.organizationCode)
      .pipe(
        takeUntil(this.onDestroy$)
      )
      .subscribe((res) => {
        this.dmIssueSetting = res;

        // 「SMS送信」且つコンソールＩＤが設定されていない場合はinfoダイアログを表示する
        if (_isNil(res.smsConsoleId) && this.data.dmInvitationMediaDiv === DmInvitationMediaDiv.Sms) {
          const s = this.dialogService.info('本機能はSMSオプションご契約後、ご利用できます。')
            .subscribe(ref => {
              ref.hide();
              s.unsubscribe();
            });
        }
      });
  }

  /**
   * クーポン発行ガイドを開く
   */
  onShowTicketGuide(event, num: number) {
    this.modalService.show(TicketIssueGuideComponent,
      { data: { organizationCode: this.data.organizationCode } }
    ).subscribe(modalRef => {
      if (modalRef.reason === ModalReason.Done) {
        // 返却値がなければ何もしない
        if (_isNil(modalRef.getResults())) {
          modalRef.hide(event.target);
          return;
        }
        const selectedTicketCode = modalRef.getResults().ticketCode;
        // 変更前と同じチケットなら何もしない
        if (this.ticketSettingList[num].ticketCode === selectedTicketCode) {
          modalRef.hide(event.target);
          return;
        }
        // 既に使用済みコードの場合はダイアログで確認する
        const isDuplicateTicket = _some(this.ticketSettingList, ticket => ticket.ticketCode === selectedTicketCode);
        if (isDuplicateTicket) {
          this.dialogService.confirm('このクーポンは既に選択されています。\n重複しますが、このまま追加してよろしいですか？').subscribe(
            dialogRef => {
              dialogRef.hide();
              if (dialogRef.reason === ModalReason.Done) {
                this.ticketSettingList[num] = modalRef.getResults();
              }
            }
          );
        } else {
          this.ticketSettingList[num] = modalRef.getResults();
        }
      }
      modalRef.hide(event.target);
    });
  }

  /**
   * クーポン 削除ボタンクリック
   */
  onClickDelete(num: number) {
    this.ticketSettingList.splice(num, 1);
  }

  /**
   * クーポン 追加ボタンクリック
   */
  onClickEdit() {
    this.ticketSettingList.push({});
  }

  /**
   * クーポン発行ガイド（抽出条件）を開く
   */
  onShowTicketGuideForSnsExtractCondition(event, num: number) {
    this.modalService.show(TicketIssueGuideComponent,
      { data: { organizationCode: this.data.organizationCode } }
    ).subscribe(modalRef => {
      if (modalRef.reason === ModalReason.Done) {
        // 返却値がなければ何もしない
        if (_isNil(modalRef.getResults())) {
          modalRef.hide(event.target);
          return;
        }
        const selectedTicketCode = modalRef.getResults().ticketCode;
        // 変更前と同じチケットなら何もしない
        if (this._snsExtractCondition.ticketSettingList[num].ticketCode === selectedTicketCode) {
          modalRef.hide(event.target);
          return;
        }
        // 既に使用済みコードの場合はダイアログで確認する
        const isDuplicateTicket = _some(this._snsExtractCondition.ticketSettingList, ticket => ticket.ticketCode === selectedTicketCode);
        if (isDuplicateTicket) {
          this.dialogService.confirm('このクーポンは既に選択されています。\n重複しますが、このまま追加してよろしいですか？').subscribe(
            dialogRef => {
              dialogRef.hide();
              if (dialogRef.reason === ModalReason.Done) {
                this._snsExtractCondition.ticketSettingList[num] = modalRef.getResults();
                this.changeUnregisteredFriendsFlg();
              }
            }
          );
        } else {
          this._snsExtractCondition.ticketSettingList[num] = modalRef.getResults();
          this.changeUnregisteredFriendsFlg();
        }
      }
      modalRef.hide(event.target);
    });
  }

  /**
   * クーポン（抽出条件） 削除ボタンクリック
   */
  onClickDeleteForSnsExtractCondition(num: number) {
    this._snsExtractCondition.ticketSettingList.splice(num, 1);
  }

  /**
   * クーポン（抽出条件） 追加ボタンクリック
   */
  onClickEditForSnsExtractCondition() {
    this._snsExtractCondition.ticketSettingList.push({});
  }

  /**
   * SNS抽出条件情報の初期化処理
   */
  private initSnsExtractCondition() {
    this._snsExtractCondition.unregisteredFriendsFlg = false;
    this._snsExtractCondition.snsFriendRegistDateFrom = null;
    this._snsExtractCondition.snsFriendRegistDateTo = null;
    this._snsExtractCondition.snsLastMessageReceiveDateTimeFrom = null;
    this._snsExtractCondition.snsLastMessageReceiveDateTimeFrom = null;
    this._snsExtractCondition.labelCodeList = [];
    this._snsExtractCondition.labelNameList = [];
    this._snsExtractCondition.searchNotTicketFlg = false;
    this._snsExtractCondition.ticketSettingList = [{}];
    this._snsExtractCondition.applyStartDate = null;
    this._snsExtractCondition.applyEndDate = null;
    this._snsExtractCondition.ticketIssueDate = null;
    this._snsExtractCondition.applyEndDateStart = null;
    this._snsExtractCondition.applyEndDateEnd = null;
  }

  /**
   * ラベルとクーポン関連のSNS抽出条件情報を初期化する
   */
  private initSnsExtractConditionToLabelAndTicket() {
    this._snsExtractCondition.labelCodeList = [];
    this._snsExtractCondition.labelNameList = [];
    this._snsExtractCondition.searchNotTicketFlg = false;
    this._snsExtractCondition.ticketSettingList = [{}];
    this._snsExtractCondition.applyStartDate = null;
    this._snsExtractCondition.applyEndDate = null;
    this._snsExtractCondition.ticketIssueDate = null;
    this._snsExtractCondition.applyEndDateStart = null;
    this._snsExtractCondition.applyEndDateEnd = null;
  }

  /**
   * SNS送信メッセージ情報の最新化処理
   */
  private updateSnsSendMessageInfo() {
    this._snsSendMessageInfo.snsDiv = SnsDiv.LINE;
    this._snsSendMessageInfo.dmExtractMenuCode = this._snsSendMessageInfo.dmContentInfo.dmExtractMenuCode;
    this._snsSendMessageInfo.dmExtractMenuDiv = DmExtractMenuDiv.Other;
    this._snsSendMessageInfo.lineMsgTemplateType = LineMsgTemplateType.FreeExtract;
    this._snsSendMessageInfo.ticketSettingList = this.ticketSettingList;
    this._snsSendMessageInfo.ticketCodeList = [];
    this._snsSendMessageInfo.ticketNameList = [];
    _forEach(this.ticketSettingList, ticketSetting => {
      if (!_isNil(ticketSetting.ticketCode)) {
        this._snsSendMessageInfo.ticketCodeList.push(ticketSetting.ticketCode);
        this._snsSendMessageInfo.ticketNameList.push(ticketSetting.ticketName);
      }
    });
    this._snsSendMessageInfo.snsExtractCondition = this._snsExtractCondition;
  }

  /**
   * 顧客未登録の友だちのみフラグの変更処理
   */
  private changeUnregisteredFriendsFlg() {
    if (this._snsExtractCondition.unregisteredFriendsFlg === true) {
      // 顧客未登録の友だちのみフラグがONの場合は無視
      return;
    }
    if (this.hasSendTargetConditionByLabelOrTicket()) {
      let dialogDispMsg = 'ラベルの条件を設定した為、\n「顧客未登録の友だちのみ」の条件をONに変更します。';
      if (this.isAvailableCouponMng) {
        dialogDispMsg = 'ラベルまたはクーポンの条件を設定した為、\n「顧客未登録の友だちのみ」の条件をONに変更します。';
      }
      // ラベルかクーポンの条件がセットされている
      this.dialogService.info(dialogDispMsg).subscribe(
        ref => {
          this._snsExtractCondition.unregisteredFriendsFlg = true;
          ref.hide();
        }
      );
    }
  }

  /**
   * SNS抽出条件のチケット情報が指定されているかのチェック
   * @returns 指定有無
   */
  private hasTicketSettingCondition(): boolean {
    let hasFlg = false;
    _forEach(this._snsExtractCondition.ticketSettingList, ticketSetting => {
      if (!_isNil(ticketSetting.ticketCode)) {
        hasFlg = true;
      }
    });
    return hasFlg;
  }

  /**
   * モーダルウィンドウ終了処理
   * @param reason
   */
  dismiss(reason: ModalReason): void {
    // 確定以外の場合はそのまま通知
    if (reason !== ModalReason.Done) {
      this.emitDismissEvent(reason);
      return;
    }
    // バリデーションチェック
    if (!this.validate()) {
      return;
    }
    this.dispSnsSendMessageConfirmModal(reason);
  }

  /**
   * SNS一斉送信確認モーダルを起動する
   */
  private dispSnsSendMessageConfirmModal(reason: ModalReason) {
    // SNS送信メッセージ情報の更新
    this.updateSnsSendMessageInfo();
    // 画像アップロード
    this.uploadImage(this._snsSendMessageInfo.dmContentInfo).pipe(mergeMap(() =>
      // SNS一斉送信確認モーダルの起動
      this.modalService.show(SnsSendMessageConfirmModalComponent, {
        data: {
          snsSendMessageInfo: this._snsSendMessageInfo,
        },
      })
    )).subscribe(modalRef => {
      modalRef.hide();
      if (modalRef.reason === ModalReason.Done) {
        this.emitDismissEvent(reason);
      }
    });
  }

  /** 顧客未登録の友だちのみチェックボックスがクリックされたとき */
  onClickUnregisteredFriendsFlg(event: Event) {
    if (this._snsExtractCondition.unregisteredFriendsFlg) {
      // ON⇒OFFのとき
      if (this.hasSendTargetConditionByLabelOrTicket()) {
        // クリックイベントの伝搬を抑止
        event.preventDefault();
        event.stopPropagation();
        let dialogDispMsg = 'ラベルの条件が設定されている為、チェックをOFFに変更出来ません。\n'
          + '「ラベル」の条件をクリアしますか？';
        if (this.isAvailableCouponMng) {
          dialogDispMsg = 'ラベルまたはクーポンの条件が設定されている為、チェックをOFFに変更出来ません。\n'
            + '「ラベル」「クーポン」の条件をクリアしますか？';
        }
        // ラベルかクーポンの条件が設定されてるとき
        this.dialogService.confirm(dialogDispMsg).subscribe(
          modalRef => {
            if (modalRef.reason === ModalReason.Done) {
              // ラベルとクーポンの条件をクリアする
              this.initSnsExtractConditionToLabelAndTicket();
              this._snsExtractCondition.unregisteredFriendsFlg = false;
              // 条件クリア済の通知
              let clearedDialogMsg = '「ラベル」の条件をクリアしました。';
              if (this.isAvailableCouponMng) {
                clearedDialogMsg = '「ラベル」「クーポン」の条件をクリアしました。';
              }
              this.dialogService.info(clearedDialogMsg).subscribe(
                infoDialogRef => {
                  infoDialogRef.hide();
                }
              );
            }
            modalRef.hide();
          }
        );
      }
    }
  }

  /** 顧客未登録の友だちのみチェックボックスが変更されたとき */
  onChangeUnregisteredFriendsFlg() {
    this._snsExtractCondition.unregisteredFriendsFlg = !this._snsExtractCondition.unregisteredFriendsFlg;
  }

  /** SNS友達登録年月日(開始) */
  onChangeSnsFriendRegistDateFrom(event: string) {
    if (event !== DateTimeUtils.initial.iso8601.date) {
      this._snsExtractCondition.snsFriendRegistDateFrom = event;
    } else {
      this._snsExtractCondition.snsFriendRegistDateFrom = null;
    }
  }
  /** SNS友達登録年月日(終了) */
  onChangeSnsFriendRegistDateTo(event: string) {
    if (event !== DateTimeUtils.initial.iso8601.date) {
      this._snsExtractCondition.snsFriendRegistDateTo = event;
    } else {
      this._snsExtractCondition.snsFriendRegistDateTo = null;
    }
  }
  /** SNS最終メッセージ受信日時(開始) */
  onChangeSnsLastMessageReceiveDateTimeFrom(event: string) {
    if (event !== DateTimeUtils.initial.iso8601.date) {
      this._snsExtractCondition.snsLastMessageReceiveDateTimeFrom = event;
    } else {
      this._snsExtractCondition.snsLastMessageReceiveDateTimeFrom = null;
    }
  }
  /** SNS最終メッセージ受信日時(終了) */
  onChangeSnsLastMessageReceiveDateTimeTo(event: string) {
    if (event !== DateTimeUtils.initial.iso8601.date) {
      this._snsExtractCondition.snsLastMessageReceiveDateTimeTo = event;
    } else {
      this._snsExtractCondition.snsLastMessageReceiveDateTimeTo = null;
    }
  }

  /**
   * ラベル変更
   * @param $event
   */
  onChangeLabel($event) {
    const labelCodeList = _cloneDeep(this._snsExtractCondition.labelCodeList);
    const labelNameList = _cloneDeep(this._snsExtractCondition.labelNameList);
    _forEach($event, value => {
      if (value.selected) {
        labelCodeList.push(value.label.labelCode);
        labelNameList.push(value.label.labelName);
      } else {
        _remove(labelCodeList, (v) => v === value.label.labelCode);
        _remove(labelNameList, (v) => v === value.label.labelName);
      }
    });
    this._snsExtractCondition.labelCodeList = labelCodeList;
    this._snsExtractCondition.labelNameList = labelNameList;
    this.changeUnregisteredFriendsFlg();
  }

  /** チケット不所持フラグ */
  onChangeSelectedSearchNotTicketFlg() {
    this._snsExtractCondition.searchNotTicketFlg = !this._snsExtractCondition.searchNotTicketFlg;
    this.changeUnregisteredFriendsFlg();
  }


  /** チケット有効期限(開始) */
  onChangeApplyStartDate(event: string) {
    if (event !== DateTimeUtils.initial.iso8601.date) {
      this._snsExtractCondition.applyStartDate = event;
    } else {
      this._snsExtractCondition.applyStartDate = null;
    }
    this.changeUnregisteredFriendsFlg();
  }

  /** チケット有効期限(終了) */
  onChangeApplyEndDate(event: string) {
    if (event !== DateTimeUtils.initial.iso8601.date) {
      this._snsExtractCondition.applyEndDate = event;
    } else {
      this._snsExtractCondition.applyEndDate = null;
    }
    this.changeUnregisteredFriendsFlg();
  }

  /** チケット発行日 */
  onChangeTicketIssueDate(event: string) {
    if (event !== DateTimeUtils.initial.iso8601.date) {
      this._snsExtractCondition.ticketIssueDate = event;
    } else {
      this._snsExtractCondition.ticketIssueDate = null;
    }
    this.changeUnregisteredFriendsFlg();
  }

  /** チケット有効期限終了日(開始) */
  onChangeApplyEndDateStart(event: string) {
    if (event !== DateTimeUtils.initial.iso8601.date) {
      this._snsExtractCondition.applyEndDateStart = event;
    } else {
      this._snsExtractCondition.applyEndDateStart = null;
    }
    this.changeUnregisteredFriendsFlg();
  }

  /** チケット有効期限終了日(終了) */
  onChangeApplyEndDateEnd(event: string) {
    if (event !== DateTimeUtils.initial.iso8601.date) {
      this._snsExtractCondition.applyEndDateEnd = event;
    } else {
      this._snsExtractCondition.applyEndDateEnd = null;
    }
    this.changeUnregisteredFriendsFlg();
  }

  /**
   * @ignore
   * 検索条件パネル条件クリアイベント
   * @param _event イベント
   */
  onSearchConditonClear(_event: Event) {
    this.initSnsExtractCondition();
  }

  /**
   * ラベルかクーポン関連の抽出条件がセットされているかを判定する
   * @returns true:セットされている、false:セットされていない
   */
  private hasSendTargetConditionByLabelOrTicket() {
    return (this._snsExtractCondition.labelCodeList.length > 0
      || this._snsExtractCondition.searchNotTicketFlg === true
      || this.hasTicketSettingCondition()
      || this._snsExtractCondition.applyStartDate != null
      || this._snsExtractCondition.applyEndDate != null
      || this._snsExtractCondition.ticketIssueDate != null
      || this._snsExtractCondition.applyEndDateStart != null
      || this._snsExtractCondition.applyEndDateEnd != null);
  }

  /**
   * バリデーションチェック
   * @returns true:OK、false:NG
   */
  private validate(): boolean {
    if (!this.blFormGroup.validate(true)) {
      return false;
    }
    if (_isNil(this._snsSendMessageInfo.snsLinkageId)) {
      this.dialogService.warn('LINE公式アカウントを登録してください。').subscribe(
        ref => ref.hide()
      );
      return false;
    }
    if (_isNil(this._snsSendMessageInfo.dmContentInfo)) {
      this.dialogService.warn('案内文を選択してください。').subscribe(
        ref => ref.hide()
      );
      return false;
    }
    if (_isNil(this._snsSendMessageInfo.snsSendTitle) || this._snsSendMessageInfo.snsSendTitle.length === 0) {
      this.dialogService.warn('一斉送信名称を入力してください。').subscribe(
        ref => ref.hide()
      );
      return false;
    }
    if (this._isSettingLineRichTemplate &&
      this._snsSendMessageInfo.dmContentInfo.dmInvitationMediaDiv === DmInvitationMediaDiv.Line) {
      const notSelectedImage = _some(
        _filter(this._dmSnsMsgSettingExList, v => v.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.Image),
        v => _isNil(v.file) && (_isNil(v.dmSnsMsgSetting.dmSnsMsgDispFile) || _isNil(v.dmSnsMsgSetting.dmSnsMsgDispFile.fileManageId))
      );
      if (notSelectedImage) {
        this.dialogService.warn('画像を設定してください。').subscribe(ref => ref.hide());
        return false;
      }
    }
    return true;
  }

  /**
   * DM案内文情報を更新する
   * @param dmContentInfo DM案内文情報
   */
  private updateDmContentInfo(dmContentInfo: IDmContentInfo) {
    this._snsSendMessageInfo.dmContentInfo = dmContentInfo;
    this._snsSendMessageInfo.dmContentNumber = dmContentInfo.dmContentNumber;
    this._snsSendMessageInfo.dmContentName = dmContentInfo.dmContentName;
    this._snsSendMessageInfo.dmContentTitle = dmContentInfo.dmContentTitle;
    this._snsSendMessageInfo.dmContentBody = dmContentInfo.dmContentBody;
    this._snsSendMessageInfo.snsSendTitle = dmContentInfo.dmContentName + ' ' + DateTimeUtils.today();
    this.setSettingLineRichTemplate();
    this.setValidate();
  }

  /** バリデーション設定 */
  private setValidate() {
    const formGroupOption: FormGroupOption = {
      ignoreRules: this._isSettingLineRichTemplate ? this.baseIgnoreRules :
        this.baseIgnoreRules.concat(this.notLineRithIgnoreRules),
    };
    this.blFormGroup = this.validatorService.getBlFormGroup(this.dmContentInfoRules, formGroupOption);
    this.applyRequiredInputDiv();
    this.blFormGroup.reset();
  }

  /** 入力必須項目を設定 */
  private applyRequiredInputDiv() {
    const validator = this.blFormGroup.getValidator('DmContentInfo#dmContentBody');
    if (validator) {
      const rule = _cloneDeep(validator.getRule());
      rule.requiredInputDiv = !this._isSettingLineRichTemplate;
      validator.updateRule(rule);
    }
  }

  /**
   * リッチ版LINEテンプレートを設定するかを判定する
   * @returns true:リッチ版LINEテンプレートを設定する、 false:リッチ版LINEテンプレートを設定しない
   */
  private setSettingLineRichTemplate() {
    const entity = this._snsSendMessageInfo.dmContentInfo;
    this._isSettingLineRichTemplate = entity.dmExtractMenuCode === DmExtractMenuCode.Customize ||
      entity.dmExtractMenuCode === DmExtractMenuCode.Other;
  }

  /**
   * DMSNSメッセージ設定が更新された時に発火
   * @param dmSnsMsgSettingExList DMSNSメッセージ設定拡張配列
   */
  onUpdateDmSnsMsgSetting(dmSnsMsgSettingExList: DmSnsMsgSettingEx[]) {
    this._dmSnsMsgSettingExList = dmSnsMsgSettingExList;
    this._snsSendMessageInfo.dmContentTitle = this._snsSendMessageInfo.dmContentInfo.dmContentTitle;
    this._snsSendMessageInfo.dmContentBody = this._snsSendMessageInfo.dmContentInfo.dmContentBody;
    this._snsSendMessageInfo.dmContentInfo.dmSnsMsgSettingList = dmSnsMsgSettingExList.map(v => v.dmSnsMsgSetting);
  }

  /**
   * 画像をアップロードします。
   */
  private uploadImage(entity: IDmContentInfo): Observable<boolean[]> {
    return this.snsSendMessageContent.saveAttachments().pipe(
      tap((dmSnsMsgSettingList) => {
        dmSnsMsgSettingList.forEach((dmSnsMsgSetting) => {
          const target = entity.dmSnsMsgSettingList.find(v => v.itemNumber === dmSnsMsgSetting.itemNumber);
          if (!_isNil(target)) {
            if (dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.FlexMessage) {
              target.dmSnsMsgSettingDetailList[0].dmSnsMsgDispFile = dmSnsMsgSetting.dmSnsMsgSettingDetailList[0].dmSnsMsgDispFile;
            } else {
              target.dmSnsMsgDispFile = dmSnsMsgSetting.dmSnsMsgDispFile;
            }
          }
        });
      }),
      mergeMap(() => {
        if (_isEmpty(entity.dmSnsMsgSettingList)) {
          return ObservableOf(null);
        }
        return ObservableForkJoin(entity.dmSnsMsgSettingList.map((dmSnsMsgSetting, index) => {
          return this.service.postImage(this._dmSnsMsgSettingExList[index].file, this._snsSendMessageInfo.snsLinkageId)
            .pipe(map((response: any) => {
              if (_isNil(response) || _isNil(response.body)) {
                return true;
              }
              const body: IImageUploadInfo = response.body;
              if (dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.FlexMessage) {
                entity.dmSnsMsgSettingList[index].dmSnsMsgSettingDetailList[0].snsImageId = body.imageId;
                entity.dmSnsMsgSettingList[index].dmSnsMsgSettingDetailList[0].snsImageUrl = body.imageUrl;
              } else {
                entity.dmSnsMsgSettingList[index].snsImageId = body.imageId;
                entity.dmSnsMsgSettingList[index].snsImageUrl = body.imageUrl;
                entity.dmSnsMsgSettingList[index].snsThumbnailImageId = body.thumbnailId;
                entity.dmSnsMsgSettingList[index].snsThumbnailImageUrl = body.thumbnailUrl;
              }
              return true;
            }));
        }));
      })
    );
  }

}



// WEBPACK FOOTER //
// ./src/app/feature/sns-send-message-modal/sns-send-message-modal.component.ts